import type { AudioSourceData, MediaSourceData } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type { AIAvatar, AudioAssetModel, SlideBackgroundModel } from 'js/src/client';
import { SlideBackgroundModelTypeEnum, SourceEnum } from 'js/src/client';
import type { PlayableAudio, VideoBackgroundModel } from 'js/src/data/media/media-types';
import type { DeepReadonly, ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS, toMutableCopy } from 'js/src/libs/consts';
import uuid from 'js/src/libs/uuid';

export function toImmutableSlideBackground(
    background: DeepReadonly<SlideBackgroundModel>,
): ImmutableMap<SlideBackgroundModel> {
    return ImmutableFromJS(
        toMutableCopy<SlideBackgroundModel>(background),
    ) as ImmutableMap<SlideBackgroundModel>;
}

export function toImmutableMediaSourceBackground(
    sourceData: DeepReadonly<MediaSourceData>,
): ImmutableMap<SlideBackgroundModel> {
    return ImmutableFromJS({
        type: sourceData.type,
        video: sourceData.type === 'video' ? sourceData : null,
        image: sourceData.type === 'image' ? sourceData : null,
        history_index_user_ids: null,
    }) as unknown as ImmutableMap<SlideBackgroundModel>;
}

export function toImmutableVideoBackground(
    video: DeepReadonly<MediaSourceData | AudioSourceData>,
): ImmutableMap<SlideBackgroundModel> {
    return ImmutableFromJS({
        type: SlideBackgroundModelTypeEnum.VIDEO,
        video,
        image: null,
    }) as unknown as ImmutableMap<SlideBackgroundModel>;
}

export function toImmutableAvatarVideoAsset(
    avatar: DeepReadonly<AIAvatar>,
): ImmutableMap<SlideBackgroundModel> {
    const previewUrl = avatar.preview_url;
    if (!previewUrl) {
        throw new Error(`Avatar ${avatar.name} does not have a preview url`);
    }
    return ImmutableFromJS({
        type: SlideBackgroundModelTypeEnum.VIDEO,
        video: {
            id: uuid(),
            uuid: uuid(),
            source: SourceEnum.SOURCE_AI_AVATAR,
            source_id: uuid(),
            license: VideoData.USER_PRIVATE_LICENSE,
            attribution: null,
            is_editorial: null,
            width: 1080,
            height: 1080,
            thumbnail_url: previewUrl,
            preview_url: previewUrl,
            render_url: null,
            poster_url: avatar.thumbnail_url,
            duration: 10000,
            has_audio: false,
            has_voiceover: true,
            ai_avatar: avatar.id,
            description: null,
            tags: [],
            title: null,
            display_credit_text: null,
            display_credit_url: null,
            captions: null,
        },
        image: null,
        icon: null,
        history_index_user_ids: [],
    }) as unknown as ImmutableMap<SlideBackgroundModel>;
}

export function toImmutableAudioAsset(
    audio: DeepReadonly<AudioAssetModel>,
): ImmutableMap<AudioAssetModel>;
export function toImmutableAudioAsset(
    audio: PlayableAudio,
): ImmutableMap<AudioAssetModel | AudioSourceData>;
export function toImmutableAudioAsset(
    audio: PlayableAudio,
): ImmutableMap<AudioAssetModel | AudioSourceData> {
    return ImmutableFromJS(toMutableCopy<AudioAssetModel | AudioSourceData>(audio)) as ImmutableMap<
        AudioAssetModel | AudioSourceData
    >;
}

export function toImmutableVoiceoverAudioAsset(
    sourceData: DeepReadonly<AudioSourceData | MediaSourceData>,
): ImmutableMap<AudioAssetModel> {
    return ImmutableFromJS({
        ...sourceData,
        has_voiceover: true,
        uuid: sourceData.uuid ?? sourceData.source_id ?? undefined,
    }) as ImmutableMap<AudioAssetModel>;
}

export type ImmutableAudioAsset = ImmutableMap<AudioAssetModel>;

export function isImmutableAudioAsset(media: unknown): media is ImmutableAudioAsset {
    return (media as ImmutableAudioAsset).get('mp3_url') !== undefined;
}

export function immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(
    backgroundAsset: ImmutableMap<SlideBackgroundModel>,
): backgroundAsset is ImmutableMap<VideoBackgroundModel> {
    return backgroundAsset.get('type') === SlideBackgroundModelTypeEnum.VIDEO;
}
